import { useCallback, useEffect, useState } from 'react'

/**
 * Per-scope localStorage keys for the floating chat card's layout (FS-561).
 *
 * The layout is scoped per board (`scope` = the board id) so resizing the chat
 * on one board never moves it on another; the Knowledge Graph page passes its
 * own fixed scope. Within a scope the layout — size (which also encodes
 * docked-right vs corner-floating, derived as `height >= innerHeight - 100`)
 * and maximized — survives navigating away and reopening.
 */
export function chatPanelLayoutStorageKey(scope: string): string {
  return `chat-panel:layout:${scope}`
}

/**
 * Width floor mirrors BoardChatPanelV2's resize clamp; height floor is a
 * sanity bound. No upper clamps: an over-tall stored height (e.g. saved on a
 * larger monitor) simply reads as the docked-right state, and the panel
 * wrapper already caps rendering at the viewport via max-width/max-height.
 */
const MIN_WIDTH_PX = 380
const MIN_HEIGHT_PX = 200

/** Resize drags call the setters per pointer-move; batch the writes. */
const PERSIST_DEBOUNCE_MS = 250

export interface ChatPanelLayoutDefaults {
  width: number
  height: number
}

export interface ChatPanelLayout {
  width: number
  setWidth: (value: number | ((prev: number) => number)) => void
  height: number
  setHeight: (value: number | ((prev: number) => number)) => void
  maximized: boolean
  setMaximized: (value: boolean | ((prev: boolean) => boolean)) => void
}

interface LayoutState {
  scope: string
  width: number
  height: number
  maximized: boolean
}

function readStoredLayout(scope: string): { width?: number; height?: number; maximized?: boolean } {
  if (typeof window === 'undefined') {
    return {}
  }
  try {
    const raw = window.localStorage.getItem(chatPanelLayoutStorageKey(scope))
    if (raw === null) {
      return {}
    }
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) {
      return {}
    }
    const { width, height, maximized } = parsed as Record<string, unknown>
    const out: { width?: number; height?: number; maximized?: boolean } = {}
    if (typeof width === 'number' && Number.isFinite(width)) {
      out.width = Math.max(MIN_WIDTH_PX, width)
    }
    if (typeof height === 'number' && Number.isFinite(height)) {
      out.height = Math.max(MIN_HEIGHT_PX, height)
    }
    if (typeof maximized === 'boolean') {
      out.maximized = maximized
    }
    return out
  } catch {
    // JSON parse error or localStorage access denied (private browsing).
    return {}
  }
}

function seedLayout(scope: string, defaults: ChatPanelLayoutDefaults): LayoutState {
  const stored = readStoredLayout(scope)
  return {
    scope,
    width: stored.width ?? defaults.width,
    height: stored.height ?? defaults.height,
    maximized: stored.maximized ?? false,
  }
}

/**
 * Persisted, per-scope layout for the floating chat card (FS-561). Behaves
 * like three `useState`s for width / height / maximized, but seeds from — and
 * writes back to — {@link chatPanelLayoutStorageKey} for the given `scope`, so
 * each board's chat layout survives unmounts independently.
 *
 * `defaults` only apply when nothing valid is stored for the scope (first
 * ever open). Writes are debounced ({@link PERSIST_DEBOUNCE_MS}) because the
 * panel's resize handles call the setters on every pointer-move, and happen
 * in an effect (not the state updater) so Strict Mode's double-invoked
 * updaters stay pure — same reasoning as `useSidebarExpanded`.
 *
 * The whole layout lives in one state object tagged with its `scope`: boards
 * can be switched without this component unmounting (route param change), and
 * deriving-state-during-render swaps in the new board's stored layout in the
 * same pass, so the persist effect can never write board A's layout under
 * board B's key.
 */
export function useChatPanelLayout(scope: string, defaults: ChatPanelLayoutDefaults): ChatPanelLayout {
  const [layout, setLayout] = useState<LayoutState>(() => seedLayout(scope, defaults))

  // Scope changed in place (board switch without unmount): re-seed from the
  // new scope's storage during render — React's sanctioned "derive state from
  // props" pattern — so this render already shows the new board's layout.
  if (layout.scope !== scope) {
    setLayout(seedLayout(scope, defaults))
  }

  useEffect(() => {
    const id = window.setTimeout(() => {
      try {
        window.localStorage.setItem(
          chatPanelLayoutStorageKey(layout.scope),
          JSON.stringify({ width: layout.width, height: layout.height, maximized: layout.maximized }),
        )
      } catch {
        // Silently ignore when storage is unavailable (private browsing) —
        // the layout just won't persist.
      }
    }, PERSIST_DEBOUNCE_MS)
    return () => window.clearTimeout(id)
  }, [layout])

  const setWidth = useCallback<ChatPanelLayout['setWidth']>((value) => {
    setLayout((prev) => ({ ...prev, width: typeof value === 'function' ? value(prev.width) : value }))
  }, [])
  const setHeight = useCallback<ChatPanelLayout['setHeight']>((value) => {
    setLayout((prev) => ({ ...prev, height: typeof value === 'function' ? value(prev.height) : value }))
  }, [])
  const setMaximized = useCallback<ChatPanelLayout['setMaximized']>((value) => {
    setLayout((prev) => ({ ...prev, maximized: typeof value === 'function' ? value(prev.maximized) : value }))
  }, [])

  return { width: layout.width, setWidth, height: layout.height, setHeight, maximized: layout.maximized, setMaximized }
}

export default useChatPanelLayout
